import React, {
    useEffect,
    useContext,
    useState,
    useCallback,
    useRef,
} from "react";
import { useLocation, useHistory } from "react-router-dom";
import dayjs from "dayjs";

import ReactPlayer from "react-player";

import { makeStyles } from "@mui/styles";
import {
    Box,
    Button,
    Chip,
    Divider,
    ListItemIcon,
    Paper,
    Skeleton,
    Tooltip,
    Typography,
} from "@mui/material";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import List from "@mui/material/List";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { IconButton } from "@mui/material";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import MuiAlert from "@mui/material/Alert";
import { Snackbar } from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import DrawOutlinedIcon from "@mui/icons-material/DrawOutlined";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

// Bootstrap
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";

// My Files
import { DatabaseContext, UserContext } from "../../context";
import firebase from "../../firebase";
import NoPermissionsMessage from "../NoPermissionsMessage";
import { canWatchPlaylist, shouldPlaylistBeVisible } from "../../utility";
import ToolbarWithLogo from "../ToolbarWithLogo";
import ConfirmDeleteDialog from "../ConfirmDeleteDialog";
import DialogWithTextInput from "../DialogWithTextInput";
import KeyDownHandler from "../KeyDownHandler";
import SharePlaylistDialog from "./SharePlaylistDialog";
import CommentSection from "../files/viewing/CommentSection";
import PlaylistListItem from "./PlaylistListItem";
import PlaylistFilterDialog from "./PlaylistFilterDialog";
import VideoControls from "./VideoControls";
import { deletePlaylistOrTimelineRecord } from "./playlistTimelineDeletion";

const useStyles = makeStyles((theme) => ({
    videoPlayer: {
        marginTop: 0,
        [theme.breakpoints.down("md")]: { marginTop: 0 },
    },
    videoColumn: {
        paddingLeft: 8,
        paddingRight: 8,
        [theme.breakpoints.down("lg")]: {
            marginBottom: 16,
        },
    },
    listColumn: {
        paddingLeft: 8,
        paddingRight: 8,
    },
    pageContainer: {
        maxWidth: 1800,
        paddingLeft: 8,
        paddingRight: 8,
        paddingBottom: 80,
    },
    embeddedPageContainer: {
        paddingBottom: 30,
        paddingLeft: 0,
        paddingRight: 0,
    },
    videoContainer: {
        position: "relative",
        paddingTop: "56.25%",
        marginTop: 10,
    },
    customFullscreenContainer: {
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: theme.zIndex.modal - 1,
        backgroundColor: "#000",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "20px",
    },
    customFullscreenVideoSection: {
        flex: 1,
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
    },
    customFullscreenVideo: {
        width: "100%",
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        "& > div": {
            width: "100%",
            height: "100%",
        },
    },
    customFullscreenAnnotation: {
        width: "100%",
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        "& img": {
            maxWidth: "100%",
            maxHeight: "100%",
            objectFit: "contain",
        },
    },
    fullscreenControls: {
        position: "absolute",
        top: 20,
        left: 20,
        zIndex: 10000,
        display: "flex",
        gap: "10px",
    },
    fullscreenButton: {
        backgroundColor: "rgba(255, 255, 255, 0.2)",
        "&:hover": {
            backgroundColor: "rgba(255, 255, 255, 0.3)",
        },
    },
    videoWrapper: {
        position: "relative",
        width: "100%",
    },
    clipNavigationContainer: {
        position: "absolute",
        bottom: "102px",
        left: "50%",
        transform: "translateX(-50%)",
        display: "flex",
        alignItems: "center",
        gap: "10px",
        padding: "12px 20px",
        backgroundColor: "rgba(0, 0, 0, 0.7)",
        borderRadius: "8px",
        zIndex: 10001,
        maxWidth: "90%",
    },
    navButton: {
        color: "white",
        backgroundColor: "rgba(255, 255, 255, 0.1)",
        "&:hover": {
            backgroundColor: "rgba(255, 255, 255, 0.2)",
        },
        "&:disabled": {
            color: "rgba(255, 255, 255, 0.3)",
        },
    },
    clipCard: {
        padding: "8px 15px",
        borderRadius: "4px",
        cursor: "pointer",
        minWidth: "150px",
        maxWidth: "200px",
        transition: "all 0.2s",
        "&:hover": {
            backgroundColor: "rgba(255, 255, 255, 0.1)",
        },
    },
    currentClipCard: {
        backgroundColor: "rgba(76, 175, 80, 0.3)",
        borderLeft: "3px solid #4caf50",
    },
    adjacentClipCard: {
        backgroundColor: "rgba(128, 128, 128, 0.2)",
        opacity: 0.7,
    },
    clipName: {
        color: "white",
        fontSize: "14px",
        fontWeight: 500,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    },
}));

const Alert = React.forwardRef(function Alert(props, ref) {
    return <MuiAlert elevation={6} variant="filled" ref={ref} {...props} />;
});

// Keyboard skip intervals (in seconds)
const SKIP_INTERVAL_NORMAL = 1.0;
const SKIP_INTERVAL_FINE = 0.1;
const SEEK_SETTLE_TIME_MS = 200;
const CLIP_END_TOLERANCE_SECONDS = 0.03;

function clipHasEnded(playbackTime, clip) {
    const outTime = Number(clip?.outTime);

    return (
        Number.isFinite(outTime) &&
        playbackTime >= outTime - CLIP_END_TOLERANCE_SECONDS
    );
}

export default function WatchPlaylistPage(props) {
    const classes = useStyles();
    const history = useHistory();
    const location = useLocation();

    const [anchorEl, setAnchorEl] = useState(null);

    const {
        files,
        filesLoaded,
        groups,
        groupsLoaded,
        users,
        usersLoaded,
        playlistUsage,
    } = useContext(DatabaseContext);

    const playerRef = useRef();
    const fullscreenPlayerRef = useRef();
    const commentPlayerRef = useRef();

    const { currentUserData } = useContext(UserContext);

    const [userHasPermission, setUserHasPermission] = useState();

    const [playing, setPlaying] = useState(false);
    const [muted, setMuted] = useState(true);

    const [playlist, setPlaylist] = useState();

    const [file, setFile] = useState({});

    const [currentClip, setCurrentClip] = useState();

    const [confirmDeleteDialogOpen, setConfirmDeleteDialogOpen] =
        useState(false);

    const [
        confirmDeleteAnnotationDialogOpen,
        setConfirmDeleteAnnotationDialogOpen,
    ] = useState(false);

    const [isDeletingAnnotation, setIsDeletingAnnotation] = useState(false);

    const [clipToDelete, setClipToDelete] = useState(null);

    const [renameDialogOpen, setRenameDialogOpen] = useState(false);
    const [shareDialogOpen, setShareDialogOpen] = useState(false);

    const [statusType, setStatusType] = useState("error");
    const [statusMessage, setStatusMessage] = useState("");
    const [statusIsVisible, setStatusIsVisible] = useState(false);

    const [isTypingComment, setIsTypingComment] = useState(false);

    const [isReorderingClips, setIsReorderingClips] = useState(false);

    const [userIsPlaylistOwner, setUserIsPlaylistOwner] = useState(false);

    const [filterDialogVisible, setFilterDialogVisible] = useState(false);

    const [ratings, setRatings] = useState([]);
    const [groupsAfterFiltering, setGroupsAfterFiltering] = useState([]);

    const [createdBy, setCreatedBy] = useState("");
    const [createdDate, setCreatedDate] = useState("");

    const [showFullPage, setShowFullPage] = useState(true);

    // New states for annotation display
    const [displayMode, setDisplayMode] = useState("video");
    const [currentAnnotationUrl, setCurrentAnnotationUrl] = useState(null);

    // Time tracking - use state for UI updates
    const [annotationCurrentTime, setAnnotationCurrentTime] = useState(0);
    const [videoCurrentTime, setVideoCurrentTime] = useState(0);

    // Refs for smooth tracking
    const currentTimeRef = useRef(0);
    const animationFrameRef = useRef(null);
    const frameCounterRef = useRef(0);
    const clipStartTimeRef = useRef(0);
    const playingClipKeyRef = useRef(null);
    const hasInitializedRef = useRef(false);
    const isSeekingRef = useRef(false);
    const lastSeekTimeRef = useRef(0);
    // Where the on-screen player should be once its media can take a seek
    const pendingSeekRef = useRef(null);

    // Custom fullscreen state
    const [customFullscreen, setCustomFullscreen] = useState(false);

    // Clip play counter to force re-renders
    const [clipPlayCounter, setClipPlayCounter] = useState(0);

    const [userCanAnnotate, setUserCanAnnotate] = useState(false);

    // Helper function to get the active player ref based on mode
    const getActivePlayerRef = useCallback(() => {
        return customFullscreen ? fullscreenPlayerRef : playerRef;
    }, [customFullscreen]);

    commentPlayerRef.current = {
        getCurrentTime: () => {
            const activePlayerRef = getActivePlayerRef();
            return activePlayerRef.current?.getCurrentTime?.() || 0;
        },
        seekTo: (seconds) => handleCommentTimestampSeek(seconds),
    };

    const getPlaylist = useCallback(
        async (key) => {
            let playlistRef = firebase.database().ref("playlists").child(key);

            if (!playlist) {
                console.log("Reading Playlist from Firebase");

                playlistRef.on("value", function (snapshot) {
                    let key = snapshot.key;
                    let data = snapshot.val();

                    let clips = [];

                    if (data && data.clips) {
                        let clipKeys = Object.keys(data.clips);
                        let clipValues = Object.values(data.clips);

                        for (let i = 0; i < clipKeys.length; i++) {
                            let clipLabels = [];
                            if (clipValues[i].labels) {
                                let labelTextValues = Object.values(
                                    clipValues[i].labels,
                                );

                                for (
                                    let j = 0;
                                    j < labelTextValues.length;
                                    j++
                                ) {
                                    clipLabels.push(labelTextValues[j]);
                                }
                            }

                            clipLabels.forEach((label) => {
                                label.filteredOutByLabel = false;
                            });

                            clips.push({
                                key: clipKeys[i],
                                index: clipValues[i].index,
                                inTime: clipValues[i].inTime,
                                outTime: clipValues[i].outTime,
                                name: clipValues[i].name,
                                note: clipValues[i].note,
                                labels: clipLabels,
                                stars: clipValues[i].stars,
                                video: clipValues[i].video,
                                type: clipValues[i].type || "video",
                                imageUrl: clipValues[i].imageUrl,
                                originalImageUrl:
                                    clipValues[i].originalImageUrl,
                                duration: clipValues[i].duration,
                                annotationData: clipValues[i].annotationData,
                                storagePath: clipValues[i].storagePath,
                                originalStoragePath:
                                    clipValues[i].originalStoragePath,
                                filteredOutByCode: false,
                                filteredOutByRating: false,
                                filteredOutByLabels: false,
                            });
                        }
                    }

                    // Groups
                    let playlistGroups = [];
                    if (data && data.groups) {
                        let groupKeys = Object.keys(data.groups);

                        for (let i = 0; i < groupKeys.length; i++) {
                            playlistGroups.push(groupKeys[i]);
                        }
                    }

                    // Users
                    let playlistUsers = [];
                    if (data && data.users) {
                        let userKeys = Object.keys(data.users);

                        for (let i = 0; i < userKeys.length; i++) {
                            playlistUsers.push(userKeys[i]);
                        }
                    }

                    if (data) {
                        let playlist = {
                            key: key,
                            clips: clips,
                            name: data.name,
                            created: data.created,
                            owner: data.owner,
                            video: data.video,
                            users: playlistUsers,
                            groups: playlistGroups,
                        };

                        setPlaylist(playlist);
                    }
                });
            } else {
                console.log("Already Read Playlist");
            }
        },
        [playlist],
    );

    useEffect(() => {
        const unsubscribe = firebase.auth().onAuthStateChanged((user) => {
            if (!user) {
                history.replace("/signin?url=" + location.pathname);
            }
        });

        // Inside a project the creator's record decides access, so wait for
        // the users list as well
        const usersAreReady = !props.projectCreatedBy || usersLoaded;

        if (currentUserData && filesLoaded && groupsLoaded && usersAreReady) {
            const urlParams = new URLSearchParams(location.search);

            let key = "";

            if (props.playlistKey) {
                key = props.playlistKey;
                setShowFullPage(false);
            } else {
                key = urlParams.get("key");
                setShowFullPage(true);
            }

            getPlaylist(key);

            if (playlist) {
                let file = files.find((f) => f.key === playlist.video);

                // Embedded in a project the user has already been let into.
                // The project page has checked that; this only needs who
                // made the project.
                const projectCreator = props.projectCreatedBy
                    ? users.find((u) => u.key === props.projectCreatedBy)
                    : undefined;

                const canWatch = canWatchPlaylist(
                    playlist,
                    file,
                    groups,
                    currentUserData,
                    projectCreator,
                );

                if (
                    canWatch ||
                    shouldPlaylistBeVisible(playlist, groups, currentUserData)
                ) {
                    if (playlist.owner === currentUserData.key) {
                        setUserIsPlaylistOwner(true);
                    } else {
                        setUserIsPlaylistOwner(false);
                    }

                    // Temp - limit users who can see annotation option.
                    if (
                        currentUserData.role === "System Digital Manager" ||
                        currentUserData.key ===
                            "oKxzF2xIAPQAddOfRJTOEfOIE2A2" ||
                        currentUserData.key === "3agBXf8IDlXdK3TIZ3BiA3vSxKT2"
                    ) {
                        setUserCanAnnotate(true);
                    } else {
                        setUserCanAnnotate(false);
                    }

                    // End Temp

                    if (playlist.owner) {
                        let ownerUser = users.find(
                            (u) => u.key === playlist.owner,
                        );
                        if (ownerUser) {
                            setCreatedBy(
                                ownerUser.firstName + " " + ownerUser.lastName,
                            );
                        }
                    }

                    if (playlist.created) {
                        setCreatedDate(
                            dayjs(playlist.created).format("MMMM D, YYYY"),
                        );
                    }

                    if (file) {
                        setFile(file);

                        if (canWatch) {
                            setUserHasPermission(true);

                            setRatings([
                                { stars: 0, selected: true },
                                { stars: 1, selected: true },
                                { stars: 2, selected: true },
                                { stars: 3, selected: true },
                            ]);

                            let playlistUsageRef = firebase
                                .database()
                                .ref("playlistusage");

                            let usageObject = playlistUsage.find(
                                (u) =>
                                    u.user === currentUserData.key &&
                                    u.playlist === playlist.key,
                            );

                            if (usageObject) {
                                playlistUsageRef
                                    .child(usageObject.key)
                                    .update({ lastOpened: dayjs().toString() });
                            } else if (playlistUsage.length > 0) {
                                playlistUsageRef.push({
                                    playlist: playlist.key,
                                    user: currentUserData.key,
                                    lastOpened: dayjs().toString(),
                                });
                            }

                            // Initialize with first clip if not already done
                            if (!hasInitializedRef.current && !currentClip) {
                                hasInitializedRef.current = true;
                                const firstClip = playlist.clips
                                    ?.sort((a, b) => a.index - b.index)
                                    ?.find((clip) => shouldShowInTable(clip));
                                if (firstClip) {
                                    initializeClip(firstClip);
                                }
                            }
                        } else {
                            setUserHasPermission(false);
                        }
                    }
                } else {
                    setUserHasPermission(false);
                }
            }
        }

        return () => unsubscribe();
        // Deliberately keyed on the data this load depends on. Listing the
        // router and lookup values it reads would re-run the whole playlist
        // fetch on unrelated renders.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentUserData, filesLoaded, groupsLoaded, usersLoaded, playlist]);

    // Clean up animation frame on unmount
    useEffect(() => {
        return () => {
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
            }
        };
    }, []);

    // Smooth progress tracking with requestAnimationFrame
    useEffect(() => {
        // Clean up existing animation
        if (animationFrameRef.current) {
            cancelAnimationFrame(animationFrameRef.current);
            animationFrameRef.current = null;
        }

        if (!currentClip || !playing) {
            console.log(
                "Not starting progress tracking - playing:",
                playing,
                "currentClip:",
                currentClip?.name,
            );
            return;
        }

        console.log("===== STARTING PROGRESS TRACKING =====");
        console.log("Clip:", currentClip.name, "Type:", currentClip.type);
        console.log(
            "InTime:",
            currentClip.inTime,
            "OutTime:",
            currentClip.outTime,
            "Duration:",
            currentClip.outTime - currentClip.inTime,
        );
        console.log("isSeekingRef:", isSeekingRef.current);
        console.log("lastSeekTimeRef:", lastSeekTimeRef.current);

        frameCounterRef.current = 0;
        playingClipKeyRef.current = currentClip.key;

        if (displayMode === "annotation") {
            const duration = currentClip.duration || 5;
            // When resuming from pause, account for already elapsed time
            clipStartTimeRef.current =
                Date.now() - currentTimeRef.current * 1000;

            const updateProgress = () => {
                if (playingClipKeyRef.current !== currentClip.key) {
                    return; // Stop if clip changed
                }

                const elapsed = (Date.now() - clipStartTimeRef.current) / 1000;
                currentTimeRef.current = elapsed;

                // Update state every 3 frames (~20fps for React updates)
                frameCounterRef.current++;
                if (frameCounterRef.current % 3 === 0) {
                    if (elapsed >= duration) {
                        setAnnotationCurrentTime(duration);
                        advanceToNextClip(currentClip);
                        return;
                    } else {
                        setAnnotationCurrentTime(elapsed);
                    }
                }

                animationFrameRef.current =
                    requestAnimationFrame(updateProgress);
            };

            animationFrameRef.current = requestAnimationFrame(updateProgress);

            return () => {
                if (animationFrameRef.current) {
                    cancelAnimationFrame(animationFrameRef.current);
                    animationFrameRef.current = null;
                }
            };
        } else if (displayMode === "video") {
            let frameCount = 0;

            const updateProgress = () => {
                if (playingClipKeyRef.current !== currentClip.key) {
                    console.log("Stopping progress - clip key changed");
                    return; // Stop if clip changed
                }

                const activePlayerRef = getActivePlayerRef();
                if (activePlayerRef.current && currentClip) {
                    const player = activePlayerRef.current.getInternalPlayer();
                    if (player && player.currentTime !== undefined) {
                        const clipDuration =
                            currentClip.outTime - currentClip.inTime;
                        const clipTime =
                            player.currentTime - currentClip.inTime;
                        currentTimeRef.current = Math.min(
                            clipDuration,
                            Math.max(0, clipTime),
                        );

                        // Log every 60 frames (about once per second at 60fps)
                        frameCount++;
                        if (frameCount % 60 === 0) {
                            console.log(
                                "Progress frame #" + frameCount,
                                "player.currentTime:",
                                player.currentTime,
                                "clipTime:",
                                clipTime,
                            );
                            console.log(
                                "  Clip range:",
                                currentClip.inTime,
                                "to",
                                currentClip.outTime,
                            );
                            console.log(
                                "  isSeekingRef:",
                                isSeekingRef.current,
                                "timeSinceSeek:",
                                Date.now() - lastSeekTimeRef.current,
                            );
                        }

                        // Update state every 3 frames (~20fps for React updates)
                        frameCounterRef.current++;
                        if (frameCounterRef.current % 3 === 0) {
                            setVideoCurrentTime(currentTimeRef.current);
                        }

                        // HLS may seek to the keyframe immediately before the
                        // requested in-point. Let playback move through that
                        // short lead-in instead of repeatedly seeking again.
                        const timeSinceSeek =
                            Date.now() - lastSeekTimeRef.current;

                        if (
                            !isSeekingRef.current &&
                            timeSinceSeek > SEEK_SETTLE_TIME_MS &&
                            clipHasEnded(player.currentTime, currentClip)
                        ) {
                            console.log("===== CLIP ENDED NATURALLY =====");
                            console.log(
                                "Clip:",
                                currentClip.name,
                                "ended at",
                                player.currentTime,
                            );
                            console.log(
                                "Expected end:",
                                currentClip.outTime,
                            );
                            advanceToNextClip(currentClip);
                            return;
                        }
                    }
                }

                animationFrameRef.current =
                    requestAnimationFrame(updateProgress);
            };

            animationFrameRef.current = requestAnimationFrame(updateProgress);

            return () => {
                console.log(
                    "Cleaning up progress tracking for:",
                    currentClip.name,
                );
                if (animationFrameRef.current) {
                    cancelAnimationFrame(animationFrameRef.current);
                    animationFrameRef.current = null;
                }
            };
        }
        // Keyed on what should restart playback tracking. currentClip is
        // covered by its key, and the helpers are rebuilt every render.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        displayMode,
        playing,
        currentClip?.key,
        clipPlayCounter,
        customFullscreen,
    ]);

    function toggleCustomFullscreen() {
        if (!customFullscreen) {
            // Entering fullscreen - activate first clip and pause
            const firstClip = playlist?.clips
                ?.sort((a, b) => a.index - b.index)
                ?.find((clip) => shouldShowInTable(clip));
            if (firstClip) {
                initializeClip(firstClip);
            }
        }
        setCustomFullscreen(!customFullscreen);
    }

    function exitCustomFullscreen() {
        setCustomFullscreen(false);
    }

    function keyWasPressed(e) {
        if (showFullPage) {
            if (
                isTypingComment ||
                shareDialogOpen ||
                renameDialogOpen ||
                confirmDeleteDialogOpen ||
                confirmDeleteAnnotationDialogOpen
            ) {
                return;
            }

            // Don't interfere with video controls (except for Tab and Escape)
            if (
                e.target.nodeName === "VIDEO" &&
                e.code !== "Tab" &&
                e.code !== "Escape"
            ) {
                return;
            }

            if (e.code === "Space") {
                e.preventDefault();
                handlePlayPause();
            }

            if (e.code === "Period" || e.key === ">") {
                e.preventDefault();
                goToNextClip();
            }

            if (e.code === "Comma" || e.key === "<") {
                e.preventDefault();
                goToPreviousClip();
            }

            if (e.code === "ArrowLeft") {
                e.preventDefault();
                // Skip backward: 1 second normally, 0.2 seconds with shift (fine control)
                const skipAmount = e.shiftKey
                    ? SKIP_INTERVAL_FINE
                    : SKIP_INTERVAL_NORMAL;
                skipBackward(skipAmount);
            }

            if (e.code === "ArrowRight") {
                e.preventDefault();
                // Skip forward: 1 second normally, 0.2 seconds with shift (fine control)
                const skipAmount = e.shiftKey
                    ? SKIP_INTERVAL_FINE
                    : SKIP_INTERVAL_NORMAL;
                skipForward(skipAmount);
            }

            if (e.code === "Escape" && customFullscreen) {
                e.preventDefault();
                exitCustomFullscreen();
            }
        }
    }

    function handlePlayPause() {
        setPlaying(!playing);
    }

    function handleMute() {
        setMuted(!muted);
    }

    function handleSeek(seekTime) {
        if (currentClip) {
            if (currentClip.type === "annotation") {
                // For annotations, seekTime is relative to clip start
                const duration = currentClip.duration || 5;
                const clampedTime = Math.max(0, Math.min(seekTime, duration));

                setAnnotationCurrentTime(clampedTime);
                currentTimeRef.current = clampedTime;
                clipStartTimeRef.current = Date.now() - clampedTime * 1000;
            } else {
                // For video clips, seekTime is relative to clip start
                // Convert to absolute video time
                const absoluteTime = currentClip.inTime + seekTime;
                const activePlayerRef = getActivePlayerRef();
                if (activePlayerRef.current) {
                    isSeekingRef.current = true;
                    lastSeekTimeRef.current = Date.now();
                    activePlayerRef.current.seekTo(absoluteTime, "seconds");
                }
                setVideoCurrentTime(seekTime);
                currentTimeRef.current = seekTime;
            }
        }
    }

    function skipBackward(seconds) {
        if (!currentClip) return;

        if (currentClip.type === "annotation") {
            const newTime = Math.max(0, annotationCurrentTime - seconds);
            handleSeek(newTime);
        } else {
            const newTime = Math.max(0, videoCurrentTime - seconds);
            handleSeek(newTime);
        }
    }

    function skipForward(seconds) {
        if (!currentClip) return;

        if (currentClip.type === "annotation") {
            const duration = currentClip.duration || 5;
            const newTime = Math.min(duration, annotationCurrentTime + seconds);
            handleSeek(newTime);
        } else {
            const clipDuration = currentClip.outTime - currentClip.inTime;
            const newTime = Math.min(clipDuration, videoCurrentTime + seconds);
            handleSeek(newTime);
        }
    }

    function advanceToNextClip(currentClip) {
        let visibleClips = playlist.clips
            .sort((a, b) => {
                if (a.index > b.index) return 1;
                if (a.index < b.index) return -1;
                return 0;
            })
            .filter((clip) => clip.index > currentClip.index);

        for (let index = 0; index < visibleClips.length; index++) {
            const clip = visibleClips[index];

            if (shouldShowInTable(clip)) {
                playClip(clip);
                return;
            }
        }

        // If we reach here, we've come to the end - pause but keep the UI visible
        setPlaying(false);
    }

    function goToPreviousClip() {
        if (!currentClip || !playlist) return;

        let visibleClips = playlist.clips
            .sort((a, b) => a.index - b.index)
            .filter(
                (clip) =>
                    shouldShowInTable(clip) && clip.index < currentClip.index,
            );

        if (visibleClips.length > 0) {
            const previousClip = visibleClips[visibleClips.length - 1];
            playClip(previousClip);
        }
    }

    function goToNextClip() {
        if (!currentClip) {
            // Start with first clip if none selected
            const firstClip = playlist?.clips
                ?.sort((a, b) => a.index - b.index)
                ?.find((clip) => shouldShowInTable(clip));
            if (firstClip) {
                playClip(firstClip);
            }
        } else {
            advanceToNextClip(currentClip);
        }
    }

    function getAdjacentClips() {
        if (!currentClip || !playlist) return { previous: null, next: null };

        const visibleClips = playlist.clips
            .filter((clip) => shouldShowInTable(clip))
            .sort((a, b) => a.index - b.index);

        const currentIndex = visibleClips.findIndex(
            (clip) => clip.key === currentClip.key,
        );

        return {
            previous: currentIndex > 0 ? visibleClips[currentIndex - 1] : null,
            next:
                currentIndex < visibleClips.length - 1
                    ? visibleClips[currentIndex + 1]
                    : null,
        };
    }

    // ReactPlayer drops a seek it gets before the media is ready: seekTo does
    // nothing until its inner player has mounted, and after that it only
    // holds the seek for five seconds waiting for a play. So the first clip's
    // in-point was lost on page load and Play ran the video from 0. Remember
    // the target, try now, and let handlePlayerReady repeat it once the
    // player can take it.
    function seekActivePlayer(seconds) {
        pendingSeekRef.current = seconds;

        const attemptSeek = (attempts = 0) => {
            const activePlayerRef = getActivePlayerRef();
            if (activePlayerRef.current) {
                activePlayerRef.current.seekTo(seconds, "seconds");
            } else if (attempts < 20) {
                // Retry up to 20 times (about 200ms total)
                setTimeout(() => attemptSeek(attempts + 1), 10);
            }
        };

        attemptSeek();
    }

    function handlePlayerReady(readyPlayer) {
        // Only the player on screen. Entering fullscreen mounts a second
        // player, which loads from the start of the video.
        if (readyPlayer !== getActivePlayerRef().current) {
            return;
        }

        // Applied once and forgotten: native HLS on iOS reports ready again
        // after every seek, and repeating this would undo the user's scrubbing
        if (pendingSeekRef.current !== null) {
            readyPlayer.seekTo(pendingSeekRef.current, "seconds");
            pendingSeekRef.current = null;
        }
    }

    function initializeClip(clip) {
        console.log("Initializing clip (paused):", clip.name);

        // Stop any existing animation
        if (animationFrameRef.current) {
            cancelAnimationFrame(animationFrameRef.current);
            animationFrameRef.current = null;
        }

        playingClipKeyRef.current = null;
        currentTimeRef.current = 0;
        frameCounterRef.current = 0;
        isSeekingRef.current = false;

        // Set state immediately
        setAnnotationCurrentTime(0);
        setVideoCurrentTime(0);
        setPlaying(false);
        setClipPlayCounter((prev) => prev + 1);

        if (clip.type === "annotation") {
            setDisplayMode("annotation");
            setCurrentAnnotationUrl(clip.imageUrl);
            setCurrentClip(clip);
        } else {
            setDisplayMode("video");
            setCurrentAnnotationUrl(null);
            setCurrentClip(clip);

            // Mark that we're seeking
            isSeekingRef.current = true;
            lastSeekTimeRef.current = Date.now();

            seekActivePlayer(clip.inTime);
        }
    }

    function playClip(clip) {
        console.log("");
        console.log("========================================");
        console.log("PLAY CLIP CALLED:", clip.name);
        console.log("Type:", clip.type);
        console.log(
            "Duration:",
            clip.type === "annotation"
                ? clip.duration
                : clip.outTime - clip.inTime,
        );
        if (clip.type !== "annotation") {
            console.log("InTime:", clip.inTime, "OutTime:", clip.outTime);
        }
        console.log("========================================");

        // Immediately stop any existing animation
        if (animationFrameRef.current) {
            console.log("Cancelling existing animation frame");
            cancelAnimationFrame(animationFrameRef.current);
            animationFrameRef.current = null;
        }

        // Mark old clip as stopped
        playingClipKeyRef.current = null;

        // Reset refs immediately
        currentTimeRef.current = 0;
        frameCounterRef.current = 0;
        isSeekingRef.current = false;

        console.log("Reset all refs and times to 0");

        // Immediately set state to 0 for instant UI update
        setAnnotationCurrentTime(0);
        setVideoCurrentTime(0);

        // Force increment to trigger new effect
        setClipPlayCounter((prev) => prev + 1);

        if (clip.type === "annotation") {
            setDisplayMode("annotation");
            setCurrentAnnotationUrl(clip.imageUrl);
            setCurrentClip(clip);
            setPlaying(true);
            console.log("Set up annotation clip, playing=true");
        } else {
            setDisplayMode("video");
            setCurrentAnnotationUrl(null);
            setCurrentClip(clip);
            setPlaying(true);

            // Mark that we're seeking
            isSeekingRef.current = true;
            lastSeekTimeRef.current = Date.now();

            console.log(
                "Set isSeekingRef=true, lastSeekTimeRef=",
                lastSeekTimeRef.current,
            );

            seekActivePlayer(clip.inTime);
        }
        console.log("");
    }

    function handleProgress(progress) {
        // Backup check in case animation frame misses the end
        // BUT also guard against stale progress events during seeks
        if (currentClip && currentClip.type !== "annotation") {
            const timeSinceSeek = Date.now() - lastSeekTimeRef.current;

            console.log(
                "handleProgress:",
                progress.playedSeconds,
                "timeSinceSeek:",
                timeSinceSeek,
                "isSeekingRef:",
                isSeekingRef.current,
            );

            // A seek may land before the requested in-point because HLS seeks
            // to keyframes. Only the out-point determines whether this clip
            // has finished.
            if (
                !isSeekingRef.current &&
                timeSinceSeek > SEEK_SETTLE_TIME_MS &&
                clipHasEnded(progress.playedSeconds, currentClip)
            ) {
                console.log("handleProgress advancing clip");
                advanceToNextClip(currentClip);
            }
        }
    }

    function handleSeekVideo(seconds, sourcePlayerRef) {
        // ReactPlayer's onSeek callback
        console.log("===== SEEK COMPLETED =====");

        // The on-screen player has landed, so handlePlayerReady has nothing
        // left to repeat. A seek finishing on the player left behind when
        // fullscreen opens must not clear the one meant for the new player.
        if (sourcePlayerRef === getActivePlayerRef()) {
            pendingSeekRef.current = null;
        }

        console.log("Seeked to:", seconds);
        console.log("Current clip:", currentClip?.name);
        console.log(
            "Expected range:",
            currentClip?.inTime,
            "to",
            currentClip?.outTime,
        );

        // Don't immediately clear isSeekingRef - wait a bit for the player to stabilize
        setTimeout(() => {
            console.log("Clearing isSeekingRef after delay");
            isSeekingRef.current = false;
        }, 100);
    }

    function handleCommentTimestampSeek(timestampSeconds) {
        const targetTime = Number(timestampSeconds);

        if (!Number.isFinite(targetTime) || !playlist) {
            return;
        }

        const matchingClip = [...playlist.clips]
            .filter(
                (clip) =>
                    clip.type !== "annotation" &&
                    targetTime >= Number(clip.inTime) - 0.05 &&
                    targetTime <= Number(clip.outTime) + 0.05,
            )
            .sort((a, b) => a.index - b.index)[0];

        if (matchingClip) {
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
                animationFrameRef.current = null;
            }

            const relativeTime = Math.max(
                0,
                targetTime - Number(matchingClip.inTime),
            );

            playingClipKeyRef.current = null;
            currentTimeRef.current = relativeTime;
            frameCounterRef.current = 0;
            setAnnotationCurrentTime(0);
            setVideoCurrentTime(relativeTime);
            setDisplayMode("video");
            setCurrentAnnotationUrl(null);
            setCurrentClip(matchingClip);
            setClipPlayCounter((previous) => previous + 1);
        } else {
            // Old comments can point outside the playlist after clip edits.
            // Keep the requested frame visible without the current clip's
            // end boundary immediately advancing playback.
            setPlaying(false);
        }

        isSeekingRef.current = true;
        lastSeekTimeRef.current = Date.now();

        seekActivePlayer(targetTime);
    }

    function handleClipSelect(index, clip, e) {
        playClip(clip);
    }

    const handleMenuOpen = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    function handleRenamePlaylistButtonPress() {
        handleMenuClose();
        setRenameDialogOpen(true);
    }

    function handleDeletePlaylistButtonPress() {
        handleMenuClose();
        setConfirmDeleteDialogOpen(true);
    }

    function handleCloseRenameDialog(newName, okPressed) {
        if (newName !== "" && okPressed) {
            let playlistsRef = firebase.database().ref("playlists");
            playlistsRef.child(playlist.key).update({ name: newName });

            let playlistInfoRef = firebase.database().ref("playlistinfo");
            playlistInfoRef.child(playlist.key).update({ name: newName });

            setPlaylist((prev) => ({
                ...prev,
                name: newName,
            }));
        }

        setRenameDialogOpen(false);
    }

    async function handleCloseDeleteDialog(result) {
        if (result === true && currentUserData) {
            try {
                await deletePlaylistOrTimelineRecord(
                    "playlist",
                    playlist.key,
                );
                history.goBack();
            } catch (error) {
                console.error("Unable to delete playlist", error);
                setStatusMessage("Unable to delete playlist");
                setStatusType("error");
                setStatusIsVisible(true);
            }
        }

        setConfirmDeleteDialogOpen(false);
    }

    function handleShareButtonPressed() {
        handleMenuClose();
        setShareDialogOpen(true);
    }

    // The dialog reports the outcome itself, in one message that covers both who
    // it was shared with and what was delivered.
    function handleCloseShareDialog() {
        setShareDialogOpen(false);
    }

    const handleStatusClose = (event, reason) => {
        if (reason === "clickaway") {
            return;
        }
        setStatusIsVisible(false);
    };

    function setIsTyping(isTyping) {
        setIsTypingComment(isTyping);
    }

    function handleReorderClipsButtonPress() {
        setPlaying(false);
        setCurrentClip(null);
        setIsReorderingClips(!isReorderingClips);
    }

    function handleMoveClipUpward(clip) {
        if (clip.index === 0) {
            return;
        }

        // Find the clip with the previous index
        const targetIndex = clip.index - 1;
        const clipToMove = playlist.clips.find((c) => c.index === targetIndex);

        if (!clipToMove) return;

        // Swap indices
        const oldIndex = clip.index;
        clipToMove.index = oldIndex;
        clip.index = targetIndex;

        let clipsRef = firebase
            .database()
            .ref("playlists")
            .child(playlist.key)
            .child("clips");

        clipsRef.child(clipToMove.key).update({ index: clipToMove.index });
        clipsRef.child(clip.key).update({ index: clip.index });
    }

    function handleMoveClipDownward(clip) {
        // Find the maximum index
        const maxIndex = Math.max(...playlist.clips.map((c) => c.index));

        if (clip.index === maxIndex) {
            return;
        }

        // Find the clip with the next index
        const targetIndex = clip.index + 1;
        const clipToMove = playlist.clips.find((c) => c.index === targetIndex);

        if (!clipToMove) return;

        // Swap indices
        const oldIndex = clip.index;
        clipToMove.index = oldIndex;
        clip.index = targetIndex;

        let clipsRef = firebase
            .database()
            .ref("playlists")
            .child(playlist.key)
            .child("clips");

        clipsRef.child(clipToMove.key).update({ index: clipToMove.index });
        clipsRef.child(clip.key).update({ index: clip.index });
    }

    function handleOpenFilterButtonPress() {
        setFilterDialogVisible(true);
    }

    function handleCloseFilterDialog(
        newFilteredCodes,
        newFilteredLabels,
        newFilteredRatings,
        groups,
    ) {
        if (newFilteredCodes) {
            playlist.clips.forEach((row) => {
                if (newFilteredCodes.includes(row.name)) {
                    row.filteredOutByCode = false;
                } else {
                    row.filteredOutByCode = true;
                }
            });
        }

        setGroupsAfterFiltering(groups);

        if (newFilteredLabels) {
            playlist.clips.forEach((row) => {
                if (row.labels.length === 0) {
                    if (newFilteredLabels.includes("(No Detail)")) {
                        row.filteredOutByLabels = false;
                    } else {
                        row.filteredOutByLabels = true;
                    }
                } else {
                    row.labels.forEach((label) => {
                        if (newFilteredLabels.includes(label.text)) {
                            label.filteredOutByLabel = false;
                        } else {
                            label.filteredOutByLabel = true;
                        }
                    });
                }
            });
        }

        if (newFilteredRatings) {
            playlist.clips.forEach((row) => {
                if (newFilteredRatings.includes(row.stars)) {
                    row.filteredOutByRating = false;
                } else {
                    row.filteredOutByRating = true;
                }
            });
        }

        setFilterDialogVisible(false);
    }

    function shouldShowInTable(clip) {
        var filteredOut = false;
        if (clip.filteredOutByCode === true) {
            filteredOut = true;
        }

        if (clip.labels && clip.labels.length === 0) {
            if (clip.filteredOutByLabels === true) {
                filteredOut = true;
            }
        } else if (clip.labels) {
            if (
                clip.labels.filter(
                    (label) => label.filteredOutByLabel === false,
                ).length === 0
            ) {
                filteredOut = true;
            }
        }

        if (clip.filteredOutByRating === true) {
            filteredOut = true;
        }

        if (filteredOut === true) {
            return false;
        }

        return true;
    }

    const handleCaptureFrame = () => {
        const activePlayerRef = getActivePlayerRef();

        if (!activePlayerRef.current || !playlist) {
            console.log("handleCaptureFrame: playerRef or playlist missing");
            return;
        }

        const player = activePlayerRef.current.getInternalPlayer();
        if (!player) {
            console.log(
                "handleCaptureFrame: internal player not available, retrying...",
            );
            // Retry after a short delay if player isn't ready yet
            setTimeout(() => handleCaptureFrame(), 100);
            return;
        }

        // Check if video is ready (readyState 2 = HAVE_CURRENT_DATA or higher)
        if (player.readyState < 2) {
            console.log(
                "handleCaptureFrame: video not ready (readyState:",
                player.readyState,
                "), retrying...",
            );
            // Retry after a short delay
            setTimeout(() => handleCaptureFrame(), 100);
            return;
        }

        // Get the actual rendered dimensions
        const videoWidth = player.videoWidth || player.clientWidth;
        const videoHeight = player.videoHeight || player.clientHeight;

        if (!videoWidth || !videoHeight) {
            console.log(
                "handleCaptureFrame: invalid dimensions",
                videoWidth,
                videoHeight,
                "retrying...",
            );
            setTimeout(() => handleCaptureFrame(), 100);
            return;
        }

        console.log(
            "handleCaptureFrame: capturing frame at",
            videoWidth,
            "x",
            videoHeight,
        );

        const canvas = document.createElement("canvas");
        canvas.width = videoWidth;
        canvas.height = videoHeight;

        const ctx = canvas.getContext("2d");
        if (!ctx) {
            console.log("handleCaptureFrame: failed to get canvas context");
            return;
        }

        ctx.drawImage(player, 0, 0, videoWidth, videoHeight);

        // Use higher quality PNG
        const dataUrl = canvas.toDataURL("image/png", 1.0);

        setPlaying(false);

        const videoKey = playlist.video || (file ? file.key : null);

        history.push({
            pathname: "/annotation",
            state: {
                capturedImage: dataUrl,
                playlistKey: playlist.key,
                playlistName: playlist.name,
                currentTime: player.currentTime,
                videoKey: videoKey,
                clipIndex: currentClip
                    ? currentClip.index
                    : playlist.clips.length,
            },
        });
    };

    const handleEditAnnotation = (clip, clipKey) => {
        if (!clip || clip.type !== "annotation") return;

        setPlaying(false);

        history.push({
            pathname: "/annotation",
            state: {
                capturedImage: clip.originalImageUrl || clip.imageUrl,
                playlistKey: playlist.key,
                playlistName: playlist.name,
                currentTime: clip.sourceTime || 0,
                videoKey: playlist.video,
                clipIndex: clip.index,
                editMode: true,
                clipKey: clipKey,
                clipName: clip.name,
                clipDuration: clip.duration || 5,
                annotationData: clip.annotationData || null,
                originalImageUrl: clip.originalImageUrl || clip.imageUrl,
            },
        });
    };

    const handleDeleteAnnotation = (clip, clipKey) => {
        if (!clip || clip.type !== "annotation") return;
        setClipToDelete({ clip, clipKey });
        setConfirmDeleteAnnotationDialogOpen(true);
    };

    const handleCloseDeleteAnnotationDialog = async (result) => {
        if (result === true && clipToDelete) {
            setIsDeletingAnnotation(true);

            const { clip, clipKey } = clipToDelete;

            try {
                const storageRef = firebase.storage().ref();

                // Delete annotated image
                if (clip.storagePath) {
                    try {
                        await storageRef.child(clip.storagePath).delete();
                        console.log(
                            `Deleted annotated image: ${clip.storagePath}`,
                        );
                    } catch (error) {
                        console.error("Error deleting annotated image:", error);
                    }
                }

                // Delete original image
                if (clip.originalStoragePath) {
                    try {
                        await storageRef
                            .child(clip.originalStoragePath)
                            .delete();
                        console.log(
                            `Deleted original image: ${clip.originalStoragePath}`,
                        );
                    } catch (error) {
                        console.error("Error deleting original image:", error);
                    }
                }

                // Delete imported images from annotationData
                if (clip.annotationData) {
                    try {
                        const annotationData =
                            typeof clip.annotationData === "string"
                                ? JSON.parse(clip.annotationData)
                                : clip.annotationData;

                        // Check if there are imported images
                        if (
                            annotationData.images &&
                            Array.isArray(annotationData.images)
                        ) {
                            for (const image of annotationData.images) {
                                if (image.storagePath) {
                                    try {
                                        await storageRef
                                            .child(image.storagePath)
                                            .delete();
                                        console.log(
                                            `Deleted imported image: ${image.storagePath}`,
                                        );
                                    } catch (error) {
                                        console.error(
                                            `Error deleting imported image ${image.storagePath}:`,
                                            error,
                                        );
                                    }
                                }
                            }
                        }
                    } catch (error) {
                        console.error(
                            "Error processing annotation data for image deletion:",
                            error,
                        );
                    }
                }

                // Delete from database
                const clipsRef = firebase
                    .database()
                    .ref("playlists")
                    .child(playlist.key)
                    .child("clips");

                await clipsRef.child(clipKey).remove();

                // Update local state
                setPlaylist((prev) => ({
                    ...prev,
                    clips: prev.clips.filter((c) => c.key !== clipKey),
                }));

                // Reset display if this was the current clip
                if (currentClip && currentClip.key === clipKey) {
                    setCurrentClip(null);
                    setDisplayMode("video");
                    setCurrentAnnotationUrl(null);
                }

                // Show success message
                setStatusMessage("Annotation deleted successfully");
                setStatusType("success");
                setStatusIsVisible(true);
            } catch (error) {
                console.error("Error deleting annotation:", error);
                setStatusMessage(
                    "Failed to delete annotation: " + error.message,
                );
                setStatusType("error");
                setStatusIsVisible(true);
            } finally {
                setIsDeletingAnnotation(false);
            }
        } else {
            setIsDeletingAnnotation(false);
        }

        setClipToDelete(null);
        setConfirmDeleteAnnotationDialogOpen(false);
    };

    // Calculate current time and duration for controls
    const getControlsProps = () => {
        if (!currentClip) {
            return {
                currentTime: 0,
                duration: 0,
            };
        }

        if (currentClip.type === "annotation") {
            return {
                currentTime: annotationCurrentTime,
                duration: currentClip.duration || 5,
            };
        } else {
            return {
                currentTime: videoCurrentTime,
                duration: currentClip.outTime - currentClip.inTime,
            };
        }
    };

    const controlsProps = getControlsProps();
    const adjacentClips = getAdjacentClips();
    const visibleClips = playlist
        ? playlist.clips
              .filter((clip) => shouldShowInTable(clip))
              .sort((a, b) => a.index - b.index)
        : [];
    const hiddenClipCount = playlist
        ? playlist.clips.length - visibleClips.length
        : 0;

    return (
        <>
            {userHasPermission === undefined && showFullPage && (
                <Box
                    sx={{
                        minHeight: "calc(100vh - 64px)",
                        backgroundColor: "background.default",
                        pt: { xs: 1.5, sm: 2.5 },
                        pb: 10,
                    }}
                >
                    <Container
                        fluid
                        style={{
                            maxWidth: 1800,
                            paddingLeft: 16,
                            paddingRight: 16,
                        }}
                    >
                        <Skeleton
                            variant="rounded"
                            width={82}
                            height={34}
                            sx={{ mb: 1.5, borderRadius: 2 }}
                        />
                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns: {
                                    xs: "1fr",
                                    xl: "minmax(0, 2fr) minmax(340px, 1fr)",
                                },
                                gap: 2,
                            }}
                        >
                            <Skeleton
                                variant="rounded"
                                width="100%"
                                sx={{ pt: "62%", borderRadius: 3 }}
                            />
                            <Skeleton
                                variant="rounded"
                                width="100%"
                                height={560}
                                sx={{ borderRadius: 3 }}
                            />
                        </Box>
                    </Container>
                </Box>
            )}
            {userHasPermission && (
                <>
                    <Box
                        sx={{
                            minHeight: showFullPage
                                ? "calc(100vh - 64px)"
                                : "auto",
                            backgroundColor: showFullPage
                                ? "background.default"
                                : "transparent",
                        }}
                    >
                    {showFullPage && (
                        <Container
                            fluid
                            style={{
                                maxWidth: 1800,
                                paddingLeft: 16,
                                paddingRight: 16,
                            }}
                        >
                            <Box
                                sx={{
                                    pt: { xs: 1.5, sm: 2.5 },
                                    mb: 1.5,
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    flexWrap: "wrap",
                                }}
                            >
                                <Button
                                    color="inherit"
                                    startIcon={<ArrowBackIcon />}
                                    onClick={() => history.goBack()}
                                    sx={{
                                        color: "text.secondary",
                                    }}
                                >
                                    Back
                                </Button>
                            </Box>
                        </Container>
                    )}
                    <Container
                        fluid
                        className={
                            showFullPage
                                ? classes.pageContainer
                                : classes.embeddedPageContainer
                        }
                    >
                        <Row>
                            <Col
                                className={classes.videoColumn}
                                xl={9}
                                lg={8}
                                md={12}
                                sm={12}
                            >
                                <Paper
                                    variant="outlined"
                                    sx={{
                                        borderRadius: 3,
                                        overflow: "hidden",
                                        borderColor: "divider",
                                    }}
                                >
                                {file && (
                                    <>
                                        <Box
                                            className={classes.videoWrapper}
                                            sx={{
                                                aspectRatio: "16 / 9",
                                                backgroundColor: "#000",
                                                overflow: "hidden",
                                            }}
                                        >
                                            {/* Video Player - always rendered, shown/hidden with CSS */}
                                            <Box
                                                sx={{
                                                    display:
                                                        displayMode === "video"
                                                            ? "block"
                                                            : "none",
                                                    width: "100%",
                                                    height: "100%",
                                                    "& > div": {
                                                        width: "100% !important",
                                                        height: "100% !important",
                                                    },
                                                }}
                                            >
                                                <ReactPlayer
                                                    className={
                                                        classes.videoPlayer
                                                    }
                                                    ref={playerRef}
                                                    url={
                                                        "https://vz-db0e7359-d70.b-cdn.net/" +
                                                        file.key +
                                                        "/playlist.m3u8"
                                                    }
                                                    autoPlay={false}
                                                    controls={false}
                                                    playing={playing}
                                                    muted={muted}
                                                    onReady={handlePlayerReady}
                                                    onProgress={handleProgress}
                                                    onSeek={(seconds) =>
                                                        handleSeekVideo(
                                                            seconds,
                                                            playerRef,
                                                        )
                                                    }
                                                    width="100%"
                                                    height="100%"
                                                />
                                            </Box>

                                            {/* Annotation Display - always rendered, shown/hidden with CSS */}
                                            <Box
                                                sx={{
                                                    display:
                                                        displayMode ===
                                                        "annotation"
                                                            ? "block"
                                                            : "none",
                                                    width: "100%",
                                                    height: "100%",
                                                    backgroundColor: "#000",
                                                    lineHeight: 0,
                                                }}
                                            >
                                                {currentAnnotationUrl && (
                                                    <img
                                                        src={
                                                            currentAnnotationUrl
                                                        }
                                                        alt="Annotation"
                                                        style={{
                                                            width: "100%",
                                                            height: "100%",
                                                            objectFit:
                                                                "contain",
                                                            display: "block",
                                                        }}
                                                    />
                                                )}
                                            </Box>

                                            {/* Custom Controls */}
                                            {currentClip && (
                                                <VideoControls
                                                    key={clipPlayCounter}
                                                    isPlaying={playing}
                                                    isMuted={muted}
                                                    currentTime={
                                                        controlsProps.currentTime
                                                    }
                                                    duration={
                                                        controlsProps.duration
                                                    }
                                                    onPlayPause={
                                                        handlePlayPause
                                                    }
                                                    onSeek={handleSeek}
                                                    onMute={handleMute}
                                                    isAnnotation={
                                                        displayMode ===
                                                        "annotation"
                                                    }
                                                />
                                            )}
                                        </Box>

                                        <Box
                                            sx={{
                                                display: "flex",
                                                alignItems: "flex-start",
                                                gap: 2,
                                                px: { xs: 2, sm: 2.5 },
                                                py: 2,
                                            }}
                                        >
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Typography
                                                    variant="h5"
                                                    component="h1"
                                                    sx={{
                                                        fontWeight: 800,
                                                        overflowWrap:
                                                            "anywhere",
                                                    }}
                                                >
                                                    {playlist.name}
                                                </Typography>
                                                {showFullPage &&
                                                    createdBy !== "" && (
                                                        <Typography
                                                            variant="body2"
                                                            color="text.secondary"
                                                            sx={{ mt: 0.5 }}
                                                        >
                                                            Created by{" "}
                                                            {createdBy}
                                                            {createdDate !==
                                                                "" &&
                                                                ` on ${createdDate}`}
                                                        </Typography>
                                                    )}
                                            </Box>
                                            <Chip
                                                size="small"
                                                label={`${
                                                    visibleClips.length
                                                } clip${
                                                    visibleClips.length === 1
                                                        ? ""
                                                        : "s"
                                                }`}
                                                sx={{
                                                    backgroundColor:
                                                        "rgba(0, 149, 61, 0.09)",
                                                    color: "primary.main",
                                                    fontWeight: 700,
                                                }}
                                            />
                                        </Box>
                                        {showFullPage && (
                                            <>
                                                <Divider />
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        flexWrap: "wrap",
                                                        gap: 1,
                                                        px: {
                                                            xs: 2,
                                                            sm: 2.5,
                                                        },
                                                        py: 1.5,
                                                    }}
                                                >
                                                    {userCanAnnotate &&
                                                        displayMode ===
                                                            "video" && (
                                                            <Button
                                                                variant="outlined"
                                                                size="small"
                                                                startIcon={
                                                                    <DrawOutlinedIcon />
                                                                }
                                                                onClick={
                                                                    handleCaptureFrame
                                                                }
                                                                sx={{
                                                                    flex: {
                                                                        xs: "1 1 calc(50% - 4px)",
                                                                        sm: "0 0 auto",
                                                                    },
                                                                }}
                                                            >
                                                                Annotate frame
                                                            </Button>
                                                        )}
                                                    <Button
                                                        variant="outlined"
                                                        size="small"
                                                        startIcon={
                                                            <FilterListIcon />
                                                        }
                                                        onClick={
                                                            handleOpenFilterButtonPress
                                                        }
                                                        aria-label="openFilterButtonPress"
                                                        sx={{
                                                            flex: {
                                                                xs: "1 1 calc(50% - 4px)",
                                                                sm: "0 0 auto",
                                                            },
                                                        }}
                                                    >
                                                        Filter
                                                        {hiddenClipCount > 0 &&
                                                            ` (${hiddenClipCount})`}
                                                    </Button>
                                                    <Tooltip title="Fullscreen">
                                                        <IconButton
                                                            onClick={
                                                                toggleCustomFullscreen
                                                            }
                                                            aria-label="toggleFullscreen"
                                                            sx={{
                                                                ml: {
                                                                    sm: "auto",
                                                                },
                                                                border: "1px solid",
                                                                borderColor: "divider",
                                                                borderRadius: 2,
                                                            }}
                                                        >
                                                            <FullscreenIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="More playlist actions">
                                                        <IconButton
                                                            onClick={
                                                                handleMenuOpen
                                                            }
                                                            aria-label="moreButtonPress"
                                                            sx={{
                                                                border: "1px solid",
                                                                borderColor: "divider",
                                                                borderRadius: 2,
                                                            }}
                                                        >
                                                            <MoreHorizIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>
                                            </>
                                        )}
                                        <Divider />
                                        <CommentSection
                                            file={playlist}
                                            videoPlayer={commentPlayerRef}
                                            setIsTyping={setIsTyping}
                                            modern
                                        />
                                    </>
                                )}
                                </Paper>
                            </Col>
                            <Col
                                className={classes.listColumn}
                                xl={3}
                                lg={4}
                                md={12}
                                sm={12}
                            >
                                <Paper
                                    variant="outlined"
                                    sx={{
                                        borderRadius: 3,
                                        overflow: "hidden",
                                        borderColor: "divider",
                                        position: showFullPage
                                            ? { xl: "sticky" }
                                            : "static",
                                        top: showFullPage
                                            ? { xl: 80 }
                                            : "auto",
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1,
                                            px: 2,
                                            py: 1.5,
                                            borderBottom: "1px solid",
                                            borderColor: "divider",
                                        }}
                                    >
                                        <Box sx={{ flex: 1 }}>
                                            <Typography
                                                variant="subtitle1"
                                                sx={{ fontWeight: 800 }}
                                            >
                                                Playlist
                                            </Typography>
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                component="div"
                                                sx={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    flexWrap: "wrap",
                                                    gap: 0.5,
                                                }}
                                            >
                                                <span>
                                                    {visibleClips.length} visible
                                                    {hiddenClipCount > 0 &&
                                                        ` · ${hiddenClipCount} hidden`}
                                                </span>
                                                <span aria-hidden="true">·</span>
                                                {["<", ">"].map((key) => (
                                                    <Box
                                                        key={key}
                                                        component="kbd"
                                                        aria-label={
                                                            key === "<"
                                                                ? "Previous clip key"
                                                                : "Next clip key"
                                                        }
                                                        sx={{
                                                            minWidth: 20,
                                                            height: 20,
                                                            px: 0.5,
                                                            display:
                                                                "inline-grid",
                                                            placeItems:
                                                                "center",
                                                            border: "1px solid",
                                                            borderColor:
                                                                "divider",
                                                            borderRadius: 1,
                                                            backgroundColor:
                                                                "background.subtle",
                                                            color:
                                                                "text.primary",
                                                            fontFamily:
                                                                "inherit",
                                                            fontSize: 12,
                                                            fontWeight: 800,
                                                            lineHeight: 1,
                                                            boxShadow:
                                                                "0 1px 0 rgba(20, 35, 25, 0.08)",
                                                        }}
                                                    >
                                                        {key}
                                                    </Box>
                                                ))}
                                                <span>change clip</span>
                                            </Typography>
                                        </Box>
                                        {showFullPage && (
                                            <Button
                                                variant={
                                                    isReorderingClips
                                                        ? "contained"
                                                        : "outlined"
                                                }
                                                size="small"
                                                startIcon={
                                                    <FormatListNumberedIcon />
                                                }
                                                onClick={
                                                    handleReorderClipsButtonPress
                                                }
                                                sx={{ flexShrink: 0 }}
                                            >
                                                {isReorderingClips
                                                    ? "Finish ordering"
                                                    : "Reorder"}
                                            </Button>
                                        )}
                                    </Box>
                                    <List
                                        disablePadding
                                        sx={{
                                            p: 1,
                                            display: "grid",
                                            gap: 0.75,
                                            maxHeight: showFullPage
                                                ? {
                                                      xs: "none",
                                                      xl: "calc(100vh - 184px)",
                                                  }
                                                : 620,
                                            overflowY: "auto",
                                        }}
                                    >
                                        {visibleClips.map((clip, index) => (
                                            <PlaylistListItem
                                                key={clip.key}
                                                clip={clip}
                                                clipKey={clip.key}
                                                index={index}
                                                isReorderingClips={
                                                    isReorderingClips
                                                }
                                                currentClip={currentClip}
                                                handleClipSelect={
                                                    handleClipSelect
                                                }
                                                handleMoveClipUpward={
                                                    handleMoveClipUpward
                                                }
                                                handleMoveClipDownward={
                                                    handleMoveClipDownward
                                                }
                                                onEditAnnotation={
                                                    handleEditAnnotation
                                                }
                                                onDeleteAnnotation={
                                                    handleDeleteAnnotation
                                                }
                                                isFullscreen={false}
                                            />
                                        ))}
                                        {visibleClips.length === 0 && (
                                            <Box
                                                sx={{
                                                    px: 2,
                                                    py: 5,
                                                    textAlign: "center",
                                                }}
                                            >
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                >
                                                    No clips match the current
                                                    filter.
                                                </Typography>
                                            </Box>
                                        )}
                                    </List>
                                </Paper>
                            </Col>
                        </Row>
                    </Container>
                    </Box>

                    {/* Hidden pre-loaded images */}
                    <Box sx={{ display: "none" }}>
                        {playlist &&
                            playlist.clips
                                .filter((clip) => clip.type === "annotation")
                                .map((clip) => (
                                    <img
                                        key={clip.key}
                                        src={clip.imageUrl}
                                        alt=""
                                        crossOrigin="anonymous"
                                    />
                                ))}
                    </Box>

                    {/* Custom Fullscreen Mode */}
                    {customFullscreen && playlist && file && (
                        <Box className={classes.customFullscreenContainer}>
                            {/* Exit Button */}
                            <Box className={classes.fullscreenControls}>
                                <Tooltip title="Exit Fullscreen (Esc)">
                                    <IconButton
                                        onClick={exitCustomFullscreen}
                                        className={classes.fullscreenButton}
                                        size="large"
                                    >
                                        <FullscreenExitIcon
                                            style={{ color: "white" }}
                                        />
                                    </IconButton>
                                </Tooltip>
                            </Box>

                            {/* Video Section */}
                            <Box
                                className={classes.customFullscreenVideoSection}
                            >
                                <Box
                                    className={classes.videoWrapper}
                                    style={{ width: "100%", height: "100%" }}
                                >
                                    {/* Video Player - Always rendered, shown/hidden with CSS */}
                                    <Box
                                        className={
                                            classes.customFullscreenVideo
                                        }
                                        sx={{
                                            display:
                                                displayMode === "video"
                                                    ? "flex"
                                                    : "none",
                                        }}
                                    >
                                        <ReactPlayer
                                            ref={fullscreenPlayerRef}
                                            url={`https://vz-db0e7359-d70.b-cdn.net/${file.key}/playlist.m3u8`}
                                            autoPlay={false}
                                            controls={false}
                                            playing={playing}
                                            muted={muted}
                                            onReady={handlePlayerReady}
                                            onProgress={handleProgress}
                                            onSeek={(seconds) =>
                                                handleSeekVideo(
                                                    seconds,
                                                    fullscreenPlayerRef,
                                                )
                                            }
                                            width="100%"
                                            height="100%"
                                        />
                                    </Box>

                                    {/* Annotation Display - Always rendered, shown/hidden with CSS */}
                                    <Box
                                        className={
                                            classes.customFullscreenAnnotation
                                        }
                                        sx={{
                                            display:
                                                displayMode === "annotation"
                                                    ? "flex"
                                                    : "none",
                                        }}
                                    >
                                        {currentAnnotationUrl && (
                                            <img
                                                src={currentAnnotationUrl}
                                                alt="Annotation"
                                            />
                                        )}
                                    </Box>

                                    {/* Clip Navigation Bar */}
                                    {currentClip && (
                                        <Box
                                            className={
                                                classes.clipNavigationContainer
                                            }
                                        >
                                            {/* Previous Button */}
                                            <IconButton
                                                onClick={goToPreviousClip}
                                                disabled={
                                                    !adjacentClips.previous
                                                }
                                                className={classes.navButton}
                                                size="small"
                                            >
                                                <ChevronLeftIcon />
                                            </IconButton>

                                            {/* Previous Clip */}
                                            {adjacentClips.previous && (
                                                <Box
                                                    className={`${classes.clipCard} ${classes.adjacentClipCard}`}
                                                    onClick={() =>
                                                        playClip(
                                                            adjacentClips.previous,
                                                        )
                                                    }
                                                >
                                                    <Typography
                                                        className={
                                                            classes.clipName
                                                        }
                                                    >
                                                        {adjacentClips.previous
                                                            .index + 1}
                                                        .{" "}
                                                        {
                                                            adjacentClips
                                                                .previous.name
                                                        }
                                                    </Typography>
                                                </Box>
                                            )}

                                            {/* Current Clip */}
                                            <Box
                                                className={`${classes.clipCard} ${classes.currentClipCard}`}
                                            >
                                                <Typography
                                                    className={classes.clipName}
                                                >
                                                    {currentClip.index + 1}.{" "}
                                                    {currentClip.name}
                                                </Typography>
                                            </Box>

                                            {/* Next Clip */}
                                            {adjacentClips.next && (
                                                <Box
                                                    className={`${classes.clipCard} ${classes.adjacentClipCard}`}
                                                    onClick={() =>
                                                        playClip(
                                                            adjacentClips.next,
                                                        )
                                                    }
                                                >
                                                    <Typography
                                                        className={
                                                            classes.clipName
                                                        }
                                                    >
                                                        {adjacentClips.next
                                                            .index + 1}
                                                        .{" "}
                                                        {
                                                            adjacentClips.next
                                                                .name
                                                        }
                                                    </Typography>
                                                </Box>
                                            )}

                                            {/* Next Button */}
                                            <IconButton
                                                onClick={goToNextClip}
                                                disabled={!adjacentClips.next}
                                                className={classes.navButton}
                                                size="small"
                                            >
                                                <ChevronRightIcon />
                                            </IconButton>
                                        </Box>
                                    )}

                                    {/* Custom Controls */}
                                    {currentClip && (
                                        <VideoControls
                                            key={clipPlayCounter}
                                            isPlaying={playing}
                                            isMuted={muted}
                                            currentTime={
                                                controlsProps.currentTime
                                            }
                                            duration={controlsProps.duration}
                                            onPlayPause={handlePlayPause}
                                            onSeek={handleSeek}
                                            onMute={handleMute}
                                            isAnnotation={
                                                displayMode === "annotation"
                                            }
                                            bottomOffset={32}
                                        />
                                    )}
                                </Box>
                            </Box>
                        </Box>
                    )}
                </>
            )}
            {userHasPermission === false && <NoPermissionsMessage />}
            <Menu
                id="menu"
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
            >
                <MenuItem onClick={handleShareButtonPressed}>
                    <ListItemIcon>
                        <ShareOutlinedIcon fontSize="small" />
                    </ListItemIcon>
                    Share playlist
                </MenuItem>
                {userIsPlaylistOwner && (
                    <MenuItem onClick={handleRenamePlaylistButtonPress}>
                        <ListItemIcon>
                            <EditOutlinedIcon fontSize="small" />
                        </ListItemIcon>
                        Rename playlist
                    </MenuItem>
                )}
                {userIsPlaylistOwner && (
                    <MenuItem
                        onClick={handleDeletePlaylistButtonPress}
                        sx={{ color: "error.main" }}
                    >
                        <ListItemIcon sx={{ color: "error.main" }}>
                            <DeleteOutlineIcon fontSize="small" />
                        </ListItemIcon>
                        Delete playlist
                    </MenuItem>
                )}
            </Menu>
            {playlist && (
                <DialogWithTextInput
                    open={renameDialogOpen}
                    onClose={handleCloseRenameDialog}
                    title={"Rename Playlist"}
                    message={"Enter a name for the Playlist."}
                    initialValue={playlist.name}
                    modern
                />
            )}
            {confirmDeleteDialogOpen && (
                <ConfirmDeleteDialog
                    title="Delete Playlist"
                    message="Are you sure you want to delete this Playlist?"
                    open={confirmDeleteDialogOpen}
                    onClose={handleCloseDeleteDialog}
                />
            )}
            {confirmDeleteAnnotationDialogOpen && (
                <ConfirmDeleteDialog
                    title="Delete Annotation"
                    message="Are you sure you want to delete this annotation? This will also remove the associated images from storage."
                    open={confirmDeleteAnnotationDialogOpen}
                    onClose={handleCloseDeleteAnnotationDialog}
                    isLoading={isDeletingAnnotation}
                />
            )}
            {filterDialogVisible && (
                <PlaylistFilterDialog
                    open={filterDialogVisible}
                    onClose={handleCloseFilterDialog}
                    rows={playlist.clips}
                    ratings={ratings}
                    groupsAfterFiltering={groupsAfterFiltering}
                />
            )}
            <SharePlaylistDialog
                open={shareDialogOpen}
                onClose={handleCloseShareDialog}
                playlist={playlist}
                file={file}
            />
            <div>
                <Snackbar
                    open={statusIsVisible}
                    autoHideDuration={6000}
                    onClose={handleStatusClose}
                >
                    <Alert onClose={handleStatusClose} severity={statusType}>
                        {statusMessage}
                    </Alert>
                </Snackbar>
            </div>
            <ToolbarWithLogo />
            <KeyDownHandler keyWasPressed={keyWasPressed} />
        </>
    );
}
